import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

export type NotificationSeverity = 'info' | 'warning' | 'action_required' | 'critical';

export type NotificationKind =
  | 'new_rlp'
  | 'low_confidence_extraction'
  | 'drafts_ready'
  | 'budget_warning'
  | 'daily_digest'
  | 'dlq_failure'
  | 'worker_error';

export interface NotificationRow {
  id: string;
  kind: NotificationKind | string;
  severity: NotificationSeverity | string;
  title: string;
  body: string | null;
  link: string | null;
  entity_type: string | null;
  entity_id: string | null;
  dedup_key: string;
  read_at: number | null;
  dismissed_at: number | null;
  action_taken_at: number | null;
  created_at: number;
  expires_at: number | null;
}

export interface UnreadCount {
  count: number;
  action_required_count: number;
}

export interface ListNotificationsParams {
  unreadOnly?: boolean;
  severity?: NotificationSeverity;
  entityType?: string;
  entityId?: string;
  showExpired?: boolean;
  limit?: number;
  before?: number;
}

export interface ListNotificationsResponse {
  notifications: NotificationRow[];
  next_before: number | null;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });
  const text = await res.text();
  if (!res.ok) throw new Error(`api ${res.status}: ${text || res.statusText}`);
  return text ? (JSON.parse(text) as T) : ({} as T);
}

function buildQuery(params: ListNotificationsParams): string {
  const qs = new URLSearchParams();
  if (params.unreadOnly) qs.set('unread', 'true');
  if (params.severity) qs.set('severity', params.severity);
  if (params.entityType) qs.set('entity_type', params.entityType);
  if (params.entityId) qs.set('entity_id', params.entityId);
  if (params.showExpired) qs.set('show_expired', 'true');
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.before != null) qs.set('before', String(params.before));
  return qs.size ? `?${qs}` : '';
}

export const notificationsApi = {
  list: (params: ListNotificationsParams = {}) =>
    request<ListNotificationsResponse>(`/api/notifications${buildQuery(params)}`),
  unreadCount: () => request<UnreadCount>('/api/notifications/unread-count'),
  markRead: (id: string) =>
    request<{ updated: boolean }>(`/api/notifications/${encodeURIComponent(id)}/read`, {
      method: 'POST',
    }),
  dismiss: (id: string) =>
    request<{ updated: boolean }>(`/api/notifications/${encodeURIComponent(id)}/dismiss`, {
      method: 'POST',
    }),
  markAllRead: () =>
    request<{ updated: number }>('/api/notifications/mark-all-read', { method: 'POST' }),
};

// Keys — colocate so invalidation stays accurate.
export const notificationKeys = {
  all: ['notifications'] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
  list: (params: ListNotificationsParams) => [...notificationKeys.all, 'list', params] as const,
};

// Cross-tab sync: one tab dismisses → sibling tabs refresh their badge.
const CHANNEL_NAME = 'notifications';
type ChannelMsg =
  | { type: 'invalidate' }
  | { type: 'read'; id: string }
  | { type: 'dismiss'; id: string };

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  return new BroadcastChannel(CHANNEL_NAME);
}

export function subscribeNotificationsChannel(qc: QueryClient): () => void {
  const channel = getChannel();
  if (!channel) return () => {};
  const handler = (ev: MessageEvent<ChannelMsg>) => {
    if (ev.data?.type) {
      qc.invalidateQueries({ queryKey: notificationKeys.all });
    }
  };
  channel.addEventListener('message', handler);
  return () => {
    channel.removeEventListener('message', handler);
    channel.close();
  };
}

function broadcast(msg: ChannelMsg): void {
  const channel = getChannel();
  if (!channel) return;
  channel.postMessage(msg);
  channel.close();
}

export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () => notificationsApi.unreadCount(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 5_000,
  });
}

export function useNotifications(params: ListNotificationsParams = {}) {
  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: () => notificationsApi.list(params),
    staleTime: 10_000,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: notificationKeys.unreadCount() });
      const prev = qc.getQueryData<UnreadCount>(notificationKeys.unreadCount());
      if (prev) {
        qc.setQueryData<UnreadCount>(notificationKeys.unreadCount(), {
          count: Math.max(0, prev.count - 1),
          action_required_count: prev.action_required_count,
        });
      }
      return { prev, id };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(notificationKeys.unreadCount(), ctx.prev);
    },
    onSettled: (_data, _err, id) => {
      qc.invalidateQueries({ queryKey: notificationKeys.all });
      broadcast({ type: 'read', id });
    },
  });
}

export function useDismiss() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.dismiss(id),
    onSettled: (_data, _err, id) => {
      qc.invalidateQueries({ queryKey: notificationKeys.all });
      broadcast({ type: 'dismiss', id });
    },
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all });
      broadcast({ type: 'invalidate' });
    },
  });
}

// Relative time helper — small enough to keep here.
export function relativeTime(ts: number, now: number = Date.now()): string {
  const s = Math.max(0, Math.round((now - ts) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.round(d / 7);
  if (w < 5) return `${w}w ago`;
  return new Date(ts).toLocaleDateString();
}

export function severityLabel(s: string): string {
  switch (s) {
    case 'critical':
      return 'Critical';
    case 'action_required':
      return 'Action required';
    case 'warning':
      return 'Warning';
    default:
      return 'Info';
  }
}
